import { useEffect, useState, useCallback } from "react";
import api, { downloadPdf } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { currentMonth } from "@/lib/periods";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { FileDown, FileText, Boxes, Users, TrendingUp, CalendarCheck, UserPlus, DollarSign, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="font-display text-2xl font-bold">{value}</div>
        </div>
      </div>
    </Card>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin || user?.role === "coordenador";
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [supId, setSupId] = useState("");
  const [supData, setSupData] = useState(null);
  const [supDownloading, setSupDownloading] = useState(false);

  const load = useCallback(() => {
    api.get("/reports/monthly/data", { params: { month } }).then((r) => setData(r.data)).catch(() => {});
  }, [month]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isAdmin) api.get("/users", { params: { role: "supervisor" } }).then((r) => setSupervisors(r.data)).catch(() => {});
  }, [isAdmin]);

  const loadSup = useCallback(() => {
    const params = { month };
    if (isAdmin && supId) params.supervisor_id = supId;
    api.get("/reports/supervisor/data", { params }).then((r) => setSupData(r.data)).catch(() => setSupData(null));
  }, [month, supId, isAdmin]);

  useEffect(() => { loadSup(); }, [loadSup]);

  const download = async () => {
    setDownloading(true);
    try {
      await downloadPdf("/reports/monthly/pdf", { month }, `relatorio-pgd-${month}.pdf`);
      toast.success("Relatório gerado!");
    } catch {
      toast.error("Erro ao gerar PDF");
    }
    setDownloading(false);
  };

  const downloadSup = async () => {
    setSupDownloading(true);
    try {
      const params = { month };
      if (isAdmin && supId) params.supervisor_id = supId;
      await downloadPdf("/reports/supervisor/pdf", params, `supervisao-${month}.pdf`);
      toast.success("Relatório de supervisão gerado!");
    } catch {
      toast.error("Erro ao gerar PDF");
    }
    setSupDownloading(false);
  };

  return (
    <div className="space-y-8 animate-fade-up" data-testid="reports-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Relatório Pastoral</h1>
          <p className="text-muted-foreground mt-1">Relatório mensal de frequência e destaques para o Pastor Sênior.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs">Mês de referência</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1.5 w-44" data-testid="report-month-input" />
          </div>
          <Button onClick={download} disabled={downloading} className="gap-2" data-testid="download-report-button">
            <FileDown className="h-4 w-4" /> {downloading ? "Gerando…" : "Baixar PDF"}
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat icon={Boxes} label="PGDs Ativos" value={data.active_groups} />
            <Stat icon={Users} label="Membros" value={data.total_members} />
            <Stat icon={TrendingUp} label="Frequência Média" value={`${data.avg_rate}%`} />
            <Stat icon={CalendarCheck} label="Encontros" value={data.total_meetings} />
            <Stat icon={UserPlus} label="Visitantes" value={data.total_visitors} />
            <Stat icon={DollarSign} label="Ofertas" value={`R$ ${data.total_offering.toFixed(2)}`} />
          </div>

          <Card className="p-6">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> Frequência por Grupo</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Grupo</TableHead>
                  <TableHead>Líder</TableHead>
                  <TableHead>Bairro</TableHead>
                  <TableHead className="text-center">Encontros</TableHead>
                  <TableHead className="text-center">Membros</TableHead>
                  <TableHead className="text-center">Frequência</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.per_group.map((p) => (
                  <TableRow key={p.name}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-muted-foreground">{p.leader}</TableCell>
                    <TableCell className="text-muted-foreground">{p.neighborhood || "—"}</TableCell>
                    <TableCell className="text-center">{p.meetings}</TableCell>
                    <TableCell className="text-center">{p.members}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={p.rate >= 75 ? "bg-[hsl(var(--emerald))]/15 text-[hsl(var(--emerald))] border-none" : "bg-primary/15 text-primary border-none"}>{p.rate}%</Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {data.per_group.every((p) => p.meetings === 0) && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nenhum encontro registrado neste mês.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      <Card className="p-6" data-testid="supervisor-report-card">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[hsl(var(--sky))]" /> Relatório de Supervisão</h3>
            <p className="text-sm text-muted-foreground mt-1">Frequência dos grupos acompanhados por cada supervisor.</p>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            {isAdmin && (
              <div>
                <Label className="text-xs">Supervisor</Label>
                <Select value={supId} onValueChange={setSupId}>
                  <SelectTrigger className="mt-1.5 w-56" data-testid="supervisor-select"><SelectValue placeholder="Eu (padrão)" /></SelectTrigger>
                  <SelectContent>
                    {supervisors.length === 0 ? (
                      <div className="px-2 py-3 text-sm text-muted-foreground text-center" data-testid="no-supervisors">Nenhum supervisor cadastrado ainda</div>
                    ) : (
                      supervisors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={downloadSup} disabled={supDownloading} variant="outline" className="gap-2" data-testid="download-supervisor-report-button">
              <FileDown className="h-4 w-4" /> {supDownloading ? "Gerando…" : "Baixar PDF"}
            </Button>
          </div>
        </div>
        {supData && (
          <div className="mt-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <Stat icon={Boxes} label="Grupos" value={supData.active_groups} />
              <Stat icon={Users} label="Membros" value={supData.total_members} />
              <Stat icon={TrendingUp} label="Frequência" value={`${supData.avg_rate}%`} />
              <Stat icon={CalendarCheck} label="Encontros" value={supData.total_meetings} />
            </div>
            {supData.active_groups === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="supervisor-empty">Nenhum grupo supervisionado{isAdmin && supId ? " por este supervisor" : ""}.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Grupo</TableHead><TableHead>Líder</TableHead><TableHead>Bairro</TableHead>
                    <TableHead className="text-center">Encontros</TableHead><TableHead className="text-center">Frequência</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {supData.per_group.map((p) => (
                    <TableRow key={p.name}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-muted-foreground">{p.leader}</TableCell>
                      <TableCell className="text-muted-foreground">{p.neighborhood || "—"}</TableCell>
                      <TableCell className="text-center">{p.meetings}</TableCell>
                      <TableCell className="text-center"><Badge className="bg-[hsl(var(--sky))]/15 text-[hsl(var(--sky))] border-none">{p.rate}%</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {supData.meeting_notes && supData.meeting_notes.length > 0 && (
              <div className="mt-6" data-testid="supervisor-notes">
                <h4 className="font-display font-semibold mb-3">Notas dos Encontros</h4>
                <div className="space-y-3">
                  {supData.meeting_notes.slice(0, 20).map((n, i) => (
                    <div key={i} className="rounded-lg border border-border p-3" data-testid="supervisor-note-item">
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <span className="font-medium">{n.group_name}</span>
                        <span className="text-xs text-muted-foreground">{n.date ? `${n.date.slice(8, 10)}/${n.date.slice(5, 7)}` : ""}</span>
                      </div>
                      {n.word_summary && <p className="text-sm text-muted-foreground mt-1"><span className="font-medium text-foreground">Resumo:</span> {n.word_summary}</p>}
                      {n.prayer_requests && <p className="text-sm text-muted-foreground mt-0.5"><span className="font-medium text-foreground">Pedidos/Decisões:</span> {n.prayer_requests}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
